import React, { useEffect, useRef, useState } from 'react'
import { motion } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Topbar } from '@/components/shared/Topbar'
import { Atmosphere } from '@/components/shared/Atmosphere'
import { openDeal, NETWORK_LABELS } from '@/services/affiliates'
import { DNA_PROFILES, DESTINATIONS } from '@/data/destinations'
import { initMap, placeMarkers, drawRoute, fitBounds, resolveStopCoordinates, DESTINATION_CENTRES } from '@/services/maps'
import { fetchImages, type Img } from '@/services/images'
import { FallbackImage } from '@/components/shared/FallbackImage'
import type { Stop, Destination } from '@/types'

// ─── Canvas fallback map — deep-espresso atlas illustration, used when Google
// Maps is unavailable (no key configured, or the JS API fails to load). ───────
function PassageCanvasMap({ stopCount }: { stopCount: number }) {
  const ref = useRef<HTMLCanvasElement>(null)
  useEffect(() => {
    const c = ref.current; if (!c) return
    const ctx = c.getContext('2d')!
    const W = c.width, H = c.height
    ctx.clearRect(0, 0, W, H)

    // Deep espresso/sepia background — matches the study-at-dusk world
    const bg = ctx.createLinearGradient(0, 0, W, H)
    bg.addColorStop(0, '#2F1B0E'); bg.addColorStop(1, '#1D1108')
    ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H)

    // Subtle grid — like old map grid lines
    ctx.strokeStyle = 'rgba(212,160,23,0.08)'; ctx.lineWidth = 0.8
    for (let x = 0; x < W; x += 40) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke() }
    for (let y = 0; y < H; y += 40) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke() }

    const pts = [[55, 185], [135, 115], [215, 80], [300, 130], [380, 178]].slice(0, stopCount)

    // Dashed gold route line — like an old atlas trade route
    ctx.beginPath(); ctx.strokeStyle = 'rgba(212,160,23,0.55)'; ctx.lineWidth = 1.8; ctx.setLineDash([5, 4])
    pts.forEach(([x, y], i) => i === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y))
    ctx.stroke(); ctx.setLineDash([])

    // Mid-point ornamental dots
    ctx.fillStyle = 'rgba(232,196,104,0.6)'
    pts.slice(0, -1).forEach(([x1, y1], i) => {
      const [x2, y2] = pts[i + 1]
      ctx.beginPath(); ctx.arc((x1 + x2) / 2, (y1 + y2) / 2, 2.5, 0, Math.PI * 2); ctx.fill()
    })

    // Teardrop-style stop markers (glow halo + gold pin + number)
    pts.forEach(([x, y], i) => {
      ctx.beginPath(); ctx.arc(x, y, 15, 0, Math.PI * 2)
      ctx.fillStyle = 'rgba(212,160,23,0.16)'; ctx.fill()
      ctx.beginPath(); ctx.arc(x, y, 9, 0, Math.PI * 2)
      ctx.fillStyle = '#D4A017'; ctx.fill()
      ctx.strokeStyle = '#241209'; ctx.lineWidth = 1.5; ctx.stroke()
      ctx.fillStyle = '#241209'; ctx.font = 'bold 10px Cinzel,serif'
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle'
      ctx.fillText(String(i + 1), x, y)
    })
  }, [stopCount])
  return <canvas ref={ref} width={430} height={250} style={{ width: '100%', height: '100%' }} />
}

// ─── Live Google Map — deep sepia/espresso styling, gold teardrop pin markers ──
// Falls back to the canvas map (via onFail) if no Maps key is configured or
// the JS API fails to load — kept silent, no console errors surfaced to user.
function PassageGoogleMap({ dest, stopImages, onFail, onStopClick }: { dest: Destination; stopImages: Record<number, Img | null>; onFail: () => void; onStopClick?: (i: number) => void }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      if (!containerRef.current) return
      try {
        const stopNames = dest.stops.map((s) => s.name)
        const resolved = await resolveStopCoordinates(stopNames, dest.name)
        if (cancelled) return
        if (resolved.length === 0) { onFail(); return }

        // Enrich resolved stops with the lazily-fetched sidebar thumbnails and
        // the stop's time-of-day/day label, for the InfoWindow photo popup.
        const enriched = resolved.map((r, i) => ({
          ...r,
          imageUrl: stopImages[i]?.thumb || stopImages[i]?.url,
          label: dest.stops[i]?.time,
        }))

        const center = DESTINATION_CENTRES[dest.id] || enriched[0].position
        const map = await initMap(containerRef.current, center, 6)
        if (cancelled) return
        await placeMarkers(map, enriched, onStopClick)
        await drawRoute(map, enriched)
        await fitBounds(map, enriched)
        setReady(true)
      } catch {
        // No key configured, or script/network failure — fall back gracefully.
        if (!cancelled) onFail()
      }
    })()
    return () => { cancelled = true }
  }, [dest.id, stopImages])

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: 220 }}>
      {!ready && (
        <div style={{ position: 'absolute', inset: 0 }}>
          <PassageCanvasMap stopCount={dest.stops.length} />
        </div>
      )}
      <div ref={containerRef} style={{ position: 'absolute', inset: 0, opacity: ready ? 1 : 0, transition: 'opacity 0.6s' }} />
    </div>
  )
}

function JourneyMap({ dest, stopImages, onStopClick }: { dest: Destination; stopImages: Record<number, Img | null>; onStopClick?: (i: number) => void }) {
  const [useFallback, setUseFallback] = useState(false)
  if (useFallback) return <PassageCanvasMap stopCount={dest.stops.length} />
  return <PassageGoogleMap dest={dest} stopImages={stopImages} onFail={() => setUseFallback(true)} onStopClick={onStopClick} />
}

export function JourneyScreen() {
  const { selectedDestination, dnaKey, setScreen, leadEmail } = useAppStore()
  const [activeStop, setActiveStop] = useState(0)
  const [stopImages, setStopImages] = useState<Record<number, Img | null>>({})
  const profile = DNA_PROFILES[dnaKey]
  const dest = selectedDestination ?? (DESTINATIONS[dnaKey]?.[0] || null)

  // Lazy-load a photo thumbnail per stop — cached via fetchImages, never blocks map render.
  useEffect(() => {
    if (!dest) return
    let cancelled = false
    setStopImages({})
    dest.stops.forEach(async (stop, i) => {
      const images = await fetchImages(`${stop.name} ${dest.name}`, 1)
      if (!cancelled) setStopImages((prev) => ({ ...prev, [i]: images[0] || null }))
    })
    return () => { cancelled = true }
  }, [dest?.id])

  if (!dest) { setScreen('dna'); return null }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      background: 'radial-gradient(ellipse at 50% 0%, #33200F 0%, #241209 55%, #180C05 100%)',
      position: 'relative',
    }}>
      <Atmosphere count={14} />
      <div style={{ position: 'relative', zIndex: 5 }}>
        <Topbar center={dest.name + ' journey'} backTo="dna" />
      </div>

      {/* Journey header */}
      <div style={{ background: 'rgba(0,0,0,0.25)', borderBottom: '1px solid rgba(212,160,23,0.18)', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 5 }}>
        <div>
          <h2 style={{ fontFamily: 'var(--serif-display)', fontSize: 20, color: '#F5EDD8', fontWeight: 400 }}>{dest.name}</h2>
          <p style={{ fontFamily: 'var(--serif-body)', fontSize: 12, color: 'var(--gold2)', marginTop: 2, fontStyle: 'italic' }}>
            {dest.stops.length} curated stops · {dest.theme.toLowerCase()}
          </p>
        </div>
        <div style={{ background: 'var(--gold2)', color: '#241209', fontFamily: 'var(--serif-caps)', fontSize: 10, padding: '5px 13px', letterSpacing: '0.06em' }}>
          Save {dest.saving}
        </div>
      </div>

      {/* Map + stops */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 220px', minHeight: 250, borderBottom: '1px solid rgba(212,160,23,0.18)', position: 'relative', zIndex: 5 }}>
        <div style={{ background: 'linear-gradient(160deg, #2F1B0E, #1D1108)', borderRight: '1px solid rgba(212,160,23,0.18)', minHeight: 220, position: 'relative' }}>
          <JourneyMap dest={dest} stopImages={stopImages} onStopClick={setActiveStop} />
        </div>
        <div style={{ overflowY: 'auto', background: 'rgba(0,0,0,0.15)' }}>
          {dest.stops.map((stop: Stop, i: number) => {
            const img = stopImages[i]
            return (
              <button key={i} onClick={() => setActiveStop(i)} style={{
                width: '100%', textAlign: 'left', padding: '10px 12px',
                borderBottom: '1px solid rgba(212,160,23,0.15)', cursor: 'pointer',
                background: activeStop === i ? 'rgba(212,160,23,0.1)' : 'transparent',
                borderLeft: activeStop === i ? '2px solid var(--gold)' : '2px solid transparent',
                display: 'flex', gap: 8,
              }}>
                {img && (
                  <FallbackImage
                    src={img.thumb || img.url}
                    alt={stop.name}
                    style={{ width: 44, height: 44, flexShrink: 0, border: '1px solid rgba(212,160,23,0.3)' }}
                    fallback={<div />}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 3 }}>
                    <div style={{ width: 16, height: 16, borderRadius: '50%', background: 'var(--gold)', color: '#241209', fontFamily: 'var(--serif-caps)', fontSize: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{i + 1}</div>
                    <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, letterSpacing: '0.1em', color: 'var(--gold2)', textTransform: 'uppercase' }}>{stop.time}</div>
                  </div>
                  <div style={{ fontFamily: 'var(--serif-display)', fontSize: 12, color: '#F5EDD8' }}>{stop.name}</div>
                  <div style={{ fontFamily: 'var(--serif-body)', fontSize: 10, color: 'rgba(245,237,216,0.55)', marginTop: 2, lineHeight: 1.4 }}>{stop.description.slice(0, 50)}…</div>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Active stop detail */}
      {dest.stops[activeStop] && (
        <motion.div key={activeStop} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          style={{ margin: '0 18px 0', padding: '16px 0', borderBottom: '1px solid rgba(212,160,23,0.18)', position: 'relative', zIndex: 5 }}>
          <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.1em', color: 'var(--gold2)', textTransform: 'uppercase', marginBottom: 4 }}>{dest.stops[activeStop].time}</div>
          <div style={{ fontFamily: 'var(--serif-display)', fontSize: 18, color: '#F5EDD8', marginBottom: 6 }}>{dest.stops[activeStop].name}</div>
          <div style={{ fontFamily: 'var(--serif-body)', fontSize: 14, color: 'rgba(245,237,216,0.72)', lineHeight: 1.75 }}>{dest.stops[activeStop].description}</div>
          <div style={{ marginTop: 8, fontFamily: 'var(--serif-caps)', fontSize: 9, color: 'var(--gold)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{dest.stops[activeStop].curatorNote}</div>
        </motion.div>
      )}

      {/* Partner deals */}
      <div style={{ padding: '12px 18px 6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'relative', zIndex: 5 }}>
        <span style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, letterSpacing: '0.12em', color: 'rgba(245,237,216,0.5)', textTransform: 'uppercase' }}>✦ Partner offers · curated for discerning travellers</span>
        <span style={{ background: 'rgba(212,160,23,0.12)', color: 'var(--gold2)', fontFamily: 'var(--serif-caps)', fontSize: 8, padding: '3px 10px', border: '1px solid rgba(212,160,23,0.3)' }}>Live rates</span>
      </div>
      <div style={{ marginBottom: 8, position: 'relative', zIndex: 5 }}>
        {dest.deals.map((deal, i) => (
          <motion.button key={i} initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}
            onClick={() => openDeal(deal, dest.name)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 12,
              padding: '10px 18px',
              background: 'transparent', border: 'none', borderBottom: '1px solid rgba(212,160,23,0.15)',
              cursor: 'pointer', textAlign: 'left',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(212,160,23,0.06)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <div style={{ width: 34, height: 34, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(212,160,23,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>{deal.icon}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: 'var(--serif-display)', fontSize: 12, color: '#F5EDD8' }}>{deal.title}</div>
              <div style={{ fontFamily: 'var(--serif-body)', fontSize: 11, color: 'rgba(245,237,216,0.58)', marginTop: 1 }}>{deal.detail}</div>
              <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 8, color: 'var(--gold2)', letterSpacing: '0.06em', marginTop: 2, textTransform: 'uppercase' }}>via {NETWORK_LABELS[deal.network] || deal.network}</div>
            </div>
            <div style={{ fontFamily: 'var(--serif-caps)', fontSize: 9, padding: '3px 8px', border: '1px solid rgba(212,160,23,0.4)', color: 'var(--gold2)', whiteSpace: 'nowrap' }}>{deal.saving}</div>
          </motion.button>
        ))}
      </div>

      {/* CTA */}
      <div style={{ padding: '12px 14px', background: 'rgba(0,0,0,0.3)', borderTop: '1px solid rgba(212,160,23,0.18)', display: 'grid', gridTemplateColumns: '1fr 1.8fr', gap: 8, marginTop: 'auto', position: 'relative', zIndex: 5 }}>
        <button onClick={() => setScreen('dna')} style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(212,160,23,0.3)', color: 'var(--gold2)', fontFamily: 'var(--serif-caps)', fontSize: 10, letterSpacing: '0.1em', padding: 11, cursor: 'pointer', textTransform: 'uppercase' }}>← Back</button>
        <motion.button onClick={() => setScreen(leadEmail ? 'card' : 'gate')} whileTap={{ scale: 0.98 }} className="p-glow-gold"
          style={{ background: 'linear-gradient(90deg, var(--gold), var(--gold2))', border: 'none', color: '#241209', fontFamily: 'var(--serif-caps)', fontSize: 11, letterSpacing: '0.1em', padding: 11, cursor: 'pointer', textTransform: 'uppercase' }}>
          Unlock my full itinerary →
        </motion.button>
      </div>
    </div>
  )
}
